import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import type { BoardSegment, ChatStoreMessage, EvaluatedLine } from "@/features/chat";
import type { BoardArrow, BoardHighlight } from "@/shared/stores/board.store";
import { PLAYED_MOVE_ARROW_COLOR, PLAYED_MOVE_SQUARE_TINT } from "@/shared/components/chessboard";
import { Chess } from "chess.js";

interface NikolaBoardPosition {
  fen: string;
  san?: string;
  arrows: readonly BoardArrow[];
  highlights: readonly BoardHighlight[];
}

export interface NikolaBoardCommand {
  key: string;
  title: string;
  moveIndex: number | null;
  positions: readonly NikolaBoardPosition[];
  step: number;
  stepCount: number;
  fen: string;
  arrows: readonly BoardArrow[];
  highlights: readonly BoardHighlight[];
  autoplay?: boolean;
  verifiedContinuation?: { moves: readonly string[]; depth: number | null };
}

const moveIndexFor = (segment: BoardSegment, moves: readonly AnalyzedMoveResult[]): number => {
  if (segment.positionRef) {
    const playerMove = moves.findIndex(
      (move) => move.moveNumber === segment.positionRef?.moveNumber && move.isPlayerMove,
    );
    return playerMove >= 0
      ? playerMove
      : moves.findIndex((move) => move.moveNumber === segment.positionRef?.moveNumber);
  }
  const before = moves.findIndex((move) => move.fenBefore === segment.fen);
  return before >= 0 ? before : moves.findIndex((move) => move.fenAfter === segment.fen);
};

const comparisonArrows = (segment: BoardSegment): BoardArrow[] =>
  segment.compare
    ? [
        { from: segment.compare.playedFrom, to: segment.compare.playedTo, color: PLAYED_MOVE_ARROW_COLOR },
        { from: segment.compare.bestFrom, to: segment.compare.bestTo, color: "#f0ece2" },
      ]
    : [];
const comparisonHighlights = (segment: BoardSegment): BoardHighlight[] =>
  segment.compare
    ? [
        { square: segment.compare.playedFrom, color: PLAYED_MOVE_SQUARE_TINT },
        { square: segment.compare.playedTo, color: PLAYED_MOVE_SQUARE_TINT },
      ]
    : [];

const linePositions = (line: EvaluatedLine): NikolaBoardPosition[] => [
  {
    fen: line.fen,
    arrows: line.steps[0] ? [{ from: line.steps[0].from, to: line.steps[0].to, color: "#f0ece2" }] : [],
    highlights: [],
  },
  ...line.steps.map((step, index) => ({
    fen: step.fenAfter,
    san: line.continuationSan[index],
    arrows: line.steps[index + 1]
      ? [{ from: line.steps[index + 1].from, to: line.steps[index + 1].to, color: "#f0ece2" }]
      : [],
    highlights: [],
  })),
];

const gamePositions = (
  segment: BoardSegment,
  moves: readonly AnalyzedMoveResult[],
  moveIndex: number,
): NikolaBoardPosition[] => {
  if (segment.plyRange) {
    const range = moves.slice(segment.plyRange.start, segment.plyRange.end + 1);
    if (range.length)
      return [
        { fen: range[0].fenBefore, arrows: [], highlights: [] },
        ...range.map((move, index) => ({
          fen: move.fenAfter,
          san: move.san,
          arrows: range[index + 1]
            ? [{ from: range[index + 1].playedFrom, to: range[index + 1].playedTo, color: "#f0ece2" }]
            : [],
          highlights: [],
        })),
      ];
  }
  const fen = moveIndex >= 0 && segment.positionRef ? moves[moveIndex].fenBefore : segment.fen;
  return [
    {
      fen,
      arrows: [...segment.arrows, ...comparisonArrows(segment)],
      highlights: [...segment.highlights, ...comparisonHighlights(segment)],
    },
  ];
};

const atStep = (command: Omit<NikolaBoardCommand, "fen" | "arrows" | "highlights">): NikolaBoardCommand => {
  const position = command.positions[command.step];
  return { ...command, fen: position.fen, arrows: position.arrows, highlights: position.highlights };
};

export const resolveNikolaBoardCommand = (
  segment: BoardSegment,
  gameId: string,
  moves: readonly AnalyzedMoveResult[],
  line?: EvaluatedLine,
): NikolaBoardCommand | null => {
  if (segment.gameId && segment.gameId !== gameId) return null;
  if (segment.positionRef && segment.positionRef.gameId !== gameId) return null;
  const moveIndex = moveIndexFor(segment, moves);
  const positions = line ? linePositions(line) : gamePositions(segment, moves, moveIndex);
  if (!positions.length) return null;
  return atStep({
    key: `${segment.positionRef?.gameId ?? gameId}:${segment.positionRef?.moveNumber ?? segment.fen}:${segment.evalLineId ?? "position"}`,
    title: segment.title ?? (line ? "Verified continuation" : "Verified position"),
    moveIndex: moveIndex >= 0 ? moveIndex : null,
    positions,
    step: 0,
    stepCount: positions.length,
    verifiedContinuation: line ? { moves: line.continuationSan, depth: line.depth } : undefined,
  });
};

export const stepNikolaBoardCommand = (command: NikolaBoardCommand, step: number): NikolaBoardCommand =>
  atStep({ ...command, step: Math.max(0, Math.min(command.stepCount - 1, step)) });

export const nikolaBoardStepSan = (
  previous: NikolaBoardCommand | null,
  current: NikolaBoardCommand | null,
): string | null => {
  if (
    !previous ||
    previous.positions !== current?.positions ||
    previous.step === current.step ||
    previous.fen === current.fen
  )
    return null;
  return current.positions[Math.max(previous.step, current.step)]?.san ?? null;
};

export const verifiedContinuationCommand = (
  gameId: string,
  moveIndex: number,
  move: AnalyzedMoveResult,
): NikolaBoardCommand | null => {
  if (!move.bestMoveSan) return null;
  const line = [move.bestMoveSan, ...(move.bestContinuation ?? [])];
  const board = new Chess(move.fenBefore);
  const steps: Array<{ fen: string; from: string; to: string; san: string }> = [];
  try {
    line.forEach((san) => {
      const played = board.move(san);
      if (!played) throw new Error("Invalid verified continuation");
      steps.push({ fen: board.fen(), from: played.from, to: played.to, san: played.san });
    });
  } catch {
    return null;
  }
  if (!steps.length) return null;
  const positions: NikolaBoardPosition[] = [
    { fen: move.fenBefore, arrows: [{ from: steps[0].from, to: steps[0].to, color: "#f0ece2" }], highlights: [] },
    ...steps.map((step, index) => ({
      fen: step.fen,
      san: step.san,
      arrows: steps[index + 1] ? [{ from: steps[index + 1].from, to: steps[index + 1].to, color: "#f0ece2" }] : [],
      highlights: [],
    })),
  ];
  return atStep({
    key: `review-line:${gameId}:${moveIndex}`,
    title: line.join(" "),
    moveIndex,
    positions,
    step: 0,
    stepCount: positions.length,
    autoplay: true,
    verifiedContinuation: { moves: line, depth: null },
  });
};

export const latestNikolaBoardSegment = (messages: readonly ChatStoreMessage[]): BoardSegment | null => {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message?.role !== "assistant") continue;
    for (let segmentIndex = (message.segments?.length ?? 0) - 1; segmentIndex >= 0; segmentIndex -= 1) {
      const segment = message.segments?.[segmentIndex];
      if (segment?.type === "board") return segment;
    }
  }
  return null;
};
